import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A radar chart, also known as a spider or web chart, is a graphical representation of data
          in which multiple variables are displayed on axes starting from the same point, forming a
          circular or polygonal shape. Each axis represents a different variable, and the data
          points for each variable are connected, creating a web-like shape. Radar charts are useful
          for comparing multiple variables across different categories, such as performance metrics
          or skill levels, and are particularly effective when you want to highlight patterns,
          strengths, or weaknesses across various dimensions of data.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Radar charts are highly effective for visualizing multivariate data in a compact and
          comparative format. Here are three key uses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Comparing Multiple Entities Across Attributes:</strong> Radar charts are ideal
            for comparing multiple entities, such as product features, employee performance, or team
            capabilities, across a consistent set of attributes or metrics.
          </li>
          <li>
            <strong>Highlighting Strengths and Weaknesses:</strong> The radial layout makes it easy
            to identify strengths and weaknesses by observing how far data points extend toward the
            outer edges, which is useful for evaluations like skill assessments or market analysis.
          </li>
          <li>
            <strong>Visualizing Complex Data in an Intuitive Format:</strong> Radar charts present
            complex datasets in a visually intuitive way, making it easier to communicate insights,
            especially in cases involving multiple variables or dimensions.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Radar charts are an engaging way to display multivariate data, but they come with
          significant limitations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Across Variables:</strong> Comparing values across
            different axes can be challenging, especially when the scales differ or overlap. This
            can lead to misinterpretation of data relationships, as the non-linear layout is less
            intuitive than linear charts.
          </li>
          <li>
            <strong>Clutter with Many Variables or Categories:</strong> Radar charts quickly become
            cluttered and hard to read when multiple data series or numerous variables are plotted.
            Overlapping lines and filled areas can obscure individual data points, making the chart
            visually overwhelming.
          </li>
          <li>
            <strong>Limited Scalability:</strong> Radar charts struggle to accommodate large
            datasets or a high number of categories. The chart becomes increasingly complex and less
            informative as the number of data points increases, limiting its usability for
            comprehensive analysis.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is crucial for radar charts, as it enhances readability and highlights key
          data points. Using distinct colors for each data series ensures clarity, especially when
          multiple datasets overlap. Transparent or semi-transparent fills can reduce visual clutter
          while allowing comparisons between overlapping areas. Gradual color gradients or varying
          line styles can emphasize trends and relationships, creating a visually engaging chart.
          Thoughtful color choices make radar charts both informative and aesthetically appealing.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Radar charts are valuable for comparing multivariate data, but they also present unique
          challenges. Here are three key challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Clutter with Multiple Variables:</strong> When displaying many data points or
            categories, overlapping lines and areas can make the chart difficult to read and
            interpret.
          </li>
          <li>
            <strong>Misleading Visual Representation:</strong> Differences in values may appear
            exaggerated or understated due to the spatial arrangement, making comparisons less
            intuitive.
          </li>
          <li>
            <strong>Limited to Comparative Analysis:</strong> Radar charts work best for relative
            comparisons but can struggle to convey absolute values effectively, limiting their
            utility in certain contexts.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
